import { useEffect, useState } from "react";
import { Armchair, Wifi, ShowerHead, UtensilsCrossed, Wine, Coffee, BedDouble, Baby, Briefcase, BatteryCharging, CheckCircle2, Settings2, Flag } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* Airport Lounges directory + "My Lounge Access" personalization (owner spec §4-7, §19). */

const AMENITY_ICONS = {
  wifi: Wifi, showers: ShowerHead, hot_food: UtensilsCrossed, bar: Wine, cocktails: Wine,
  wine: Wine, coffee: Coffee, sleeping_rooms: BedDouble, quiet_area: BedDouble,
  family_room: Baby, kids_area: Baby, workspaces: Briefcase, charging: BatteryCharging,
};
const AMENITY_LABELS = {
  wifi: "Wi-Fi", hot_food: "Hot food", snacks: "Snacks", bar: "Bar", wine: "Wine", cocktails: "Cocktails",
  coffee: "Coffee", showers: "Showers", restrooms: "Restrooms", sleeping_rooms: "Sleep rooms",
  quiet_area: "Quiet area", workspaces: "Workspaces", charging: "Charging", family_room: "Family room",
  kids_area: "Kids area", tv: "TV", flight_screens: "Flight screens", newspapers: "News",
  prayer_room: "Prayer room", spa: "Spa", smoking_area: "Smoking", outdoor_terrace: "Terrace",
};

function LoungeCard({ l, myPrograms, onReport }) {
  const likely = l.programs?.some((p) => myPrograms.includes(p));
  return (
    <div className={`border bg-white p-5 ${likely ? "border-emerald-500 border-2" : "border-zinc-300"}`}
      data-testid={`lounge-${l.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h4 className="font-heading font-700 text-lg leading-snug">{l.name}</h4>
          <div className="text-sm text-muted-foreground mt-0.5">
            {[l.terminal, l.concourse && `Concourse ${l.concourse}`, l.gate_area,
              l.airside === true ? "Airside" : l.airside === false ? "Landside" : null]
              .filter(Boolean).join(" · ") || "Location — check airport signage"}
          </div>
        </div>
        {likely && <span className="shrink-0 inline-flex items-center gap-1 text-xs font-700 bg-emerald-600 text-white px-2 py-1" data-testid="likely-access-badge"><CheckCircle2 className="w-3 h-3" /> Likely access</span>}
      </div>
      {l.description && <p className="text-sm text-zinc-700 mt-2">{l.description}</p>}
      {l.programs?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {l.programs.map((p) => (
            <span key={p} className={`text-xs border px-2 py-0.5 ${myPrograms.includes(p) ? "border-emerald-500 bg-emerald-50 text-emerald-800 font-700" : "border-zinc-300 bg-zinc-50 text-zinc-700"}`}>{p}</span>
          ))}
        </div>
      )}
      {l.amenities?.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-xs text-zinc-600">
          {l.amenities.slice(0, 8).map((a) => {
            const Icon = AMENITY_ICONS[a] || Armchair;
            return <span key={a} className="inline-flex items-center gap-1"><Icon className="w-3.5 h-3.5 text-violet-600" />{AMENITY_LABELS[a] || a}</span>;
          })}
        </div>
      )}
      <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
        <span>{l.hours || ""}{l.guest_policy ? `${l.hours ? " · " : ""}${l.guest_policy}` : ""}</span>
        <button onClick={() => onReport(l)} className="inline-flex items-center gap-1 hover:text-zinc-800 underline" data-testid={`lounge-report-${l.id}`}>
          <Flag className="w-3 h-3" /> Suggest an update
        </button>
      </div>
    </div>
  );
}

function MyAccessEditor({ programs, myPrograms, onSave, onClose }) {
  const [sel, setSel] = useState(myPrograms);
  return (
    <div className="border border-violet-300 bg-violet-50/60 p-5" data-testid="my-access-editor">
      <div className="font-heading font-900 mb-1">My Lounge Access</div>
      <p className="text-sm text-zinc-700 mb-3">Select the programs and memberships you hold — lounges you can likely access get highlighted at every airport.</p>
      <div className="grid sm:grid-cols-2 gap-1.5 mb-4">
        {programs.map((p) => (
          <label key={p} className="flex items-center gap-2 text-sm bg-white border border-zinc-200 px-3 py-2 cursor-pointer hover:border-violet-400">
            <input type="checkbox" checked={sel.includes(p)} data-testid={`program-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onChange={(e) => setSel(e.target.checked ? [...sel, p] : sel.filter((x) => x !== p))} />
            {p}
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave(sel)} data-testid="my-access-save" className="bg-violet-700 text-white px-4 py-2 text-sm font-700 hover:bg-violet-600">Save my access</button>
        <button onClick={onClose} className="border border-zinc-300 px-4 py-2 text-sm">Cancel</button>
      </div>
    </div>
  );
}

export default function AirportLounges({ iata, onReport }) {
  const [data, setData] = useState(null);
  const [allPrograms, setAllPrograms] = useState([]);
  const [myPrograms, setMyPrograms] = useState([]);
  const [editing, setEditing] = useState(false);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    setData(null);
    api.get(`/airports/${iata}/lounges`).then((r) => setData(r.data)).catch(() => setData({ groups: [], count: 0 }));
    api.get("/lounge-programs").then((r) => setAllPrograms(r.data.programs)).catch(() => {});
    api.get("/me/lounge-access").then((r) => { setMyPrograms(r.data.programs); setSignedIn(true); }).catch(() => setSignedIn(false));
  }, [iata]);

  const save = async (sel) => {
    try {
      const r = await api.put("/me/lounge-access", { programs: sel });
      setMyPrograms(r.data.programs); setEditing(false);
      toast.success("Lounge access saved — matching lounges are now highlighted");
    } catch { toast.error("Please sign in to save your lounge access"); }
  };

  if (!data) return <div className="py-10 text-center text-muted-foreground text-sm">Loading lounges…</div>;
  const likelyCount = data.groups?.reduce((n, g) => n + g.items.filter((l) => l.programs?.some((p) => myPrograms.includes(p))).length, 0) || 0;

  return (
    <div className="space-y-6" data-testid="airport-lounges-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading font-900 text-2xl sm:text-3xl flex items-center gap-2">
          <Armchair className="w-7 h-7 text-violet-700" /> Airport Lounges
        </h2>
        <button onClick={() => setEditing((e) => !e)} data-testid="my-access-toggle"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-700 border border-zinc-300 bg-white hover:border-violet-500 transition-colors">
          <Settings2 className="w-4 h-4" /> My Lounge Access {myPrograms.length > 0 && `(${myPrograms.length})`}
        </button>
      </div>
      {editing && <MyAccessEditor programs={allPrograms} myPrograms={myPrograms} onSave={save} onClose={() => setEditing(false)} />}
      {myPrograms.length > 0 && data.count > 0 && (
        <p className="text-sm font-600 text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="likely-access-summary">
          {likelyCount > 0 ? `${likelyCount} lounge${likelyCount === 1 ? "" : "s"} here match your saved access — highlighted below. Access is likely, not guaranteed; verify before travel.`
            : "None of the lounges here match your saved programs — paid entry may still be available."}
        </p>
      )}
      {data.count === 0 ? (
        <div className="border border-amber-300 bg-amber-50 p-5" data-testid="lounges-fallback">
          <p className="text-sm text-amber-900">
            Lounge research for this airport is in progress. Check the airport's official website for current lounge
            information — and if you know a lounge here, use "Suggest an update" on any airport page to help fellow travelers.
          </p>
        </div>
      ) : (
        data.groups.map((g) => (
          <div key={g.key} data-testid={`lounge-group-${g.key}`}>
            <h3 className="font-heading font-700 text-lg mb-3">{g.label} <span className="text-sm text-muted-foreground font-400">({g.items.length})</span></h3>
            <div className="grid md:grid-cols-2 gap-4">
              {g.items.map((l) => <LoungeCard key={l.id} l={l} myPrograms={myPrograms} onReport={onReport} />)}
            </div>
          </div>
        ))
      )}
      {data.count > 0 && <p className="text-xs text-muted-foreground">{data.disclaimer}</p>}
      {!signedIn && <p className="text-xs text-muted-foreground">Sign in to save your lounge memberships and see which lounges you can likely access at every airport.</p>}
    </div>
  );
}
